import {useCallback, useEffect, useMemo, useState} from 'react';
import {EntryRepository, SaveParams} from './modules/entry-repository';
import {getEntryVars} from "@pages/entry/modules/functions";
import {useMapStore} from "@pages/entry/stores/useMapStore";
import {useEntryStore} from "@pages/entry/stores/useEntryStore";
import {useEntryErrorStore} from "@pages/entry/stores/useEntryErrorStore";
import {EntryValidator} from "@pages/entry/modules/EntryValidator";

export const useEntryApp = () => {

    const entryRepository = useMemo(() => new EntryRepository(getEntryVars()), []);
    const validator = useMemo(() => {
        const v = new EntryValidator();
        v.setStoreGetter(() => useEntryStore.getState());
        return v;
    }, []);


    const setOptionMap = useMapStore((s) => s.setOptionMap);
    const company_name = useEntryStore((s) => s.company_name);
    const your_name = useEntryStore((s) => s.your_name);
    const email = useEntryStore((s) => s.email);
    const tel = useEntryStore((s) => s.tel);
    const project_detail_section = useEntryStore((s) => s.project_detail_section);
    const project_detail_section_other_text = useEntryStore((s) => s.project_detail_section_other_text);
    const yoto_naiyo1 = useEntryStore((s) => s.yoto_naiyo1);
    const yoto_menseki1 = useEntryStore((s) => s.yoto_menseki1);
    const shinsakikan_sinseitaio = useEntryStore((s) => s.shinsakikan_sinseitaio);
    const mitsumori_iraijino_kibonoki = useEntryStore((s) => s.mitsumori_iraijino_kibonoki);
    const files = useEntryStore((s) => s.files);
    const agree = useEntryStore((s) => s.agree);

    const setStoreValue = useEntryStore((s) => s.setValue);
    const getStoreValues = useEntryStore((s) => s.getValues);

    const setTestData = useEntryStore((s) => s.setTestData);

    const project_detail_section_options = useMapStore((s) => s.project_detail_section);
    const shinsakikan_sinseitaio_options = useMapStore((s) => s.shinsakikan_sinseitaio);
    const mitsumori_iraijino_kibonoki_options = useMapStore((s) => s.mitsumori_iraijino_kibonoki);


    const company_name_error = useEntryErrorStore((s) => s.company_name);
    const your_name_error = useEntryErrorStore((s) => s.your_name);
    const email_error = useEntryErrorStore((s) => s.email);
    const tel_error = useEntryErrorStore((s) => s.tel);
    const project_detail_section_error = useEntryErrorStore((s) => s.project_detail_section);
    const yoto_naiyo1_error = useEntryErrorStore((s) => s.yoto_naiyo1);
    const yoto_menseki1_error = useEntryErrorStore((s) => s.yoto_menseki1);
    const shinsakikan_sinseitaio_error = useEntryErrorStore((s) => s.shinsakikan_sinseitaio);
    const mitsumori_iraijino_kibonoki_error = useEntryErrorStore((s) => s.mitsumori_iraijino_kibonoki);

    const agree_error = useEntryErrorStore((s) => s.agree);

    const setErrorValue = useEntryErrorStore((s) => s.setValue);

    const [errorCount, setErrorCount] = useState(0);

    const [isProcessing, setIsProcessing] = useState(false);
    const [isComplete, setIsComplete] = useState(false);

    useEffect(() => {
        const load = (async () => {
            const initialRes = await entryRepository.initialLoad();

            setOptionMap(initialRes.map);

            if (Object.keys(initialRes.testData).length > 0) {
                setTestData(initialRes.testData);
            }
        });
        load();

    }, []);

    const setValue = (name: string, value: string | boolean) => {
        setStoreValue(name, value);

        const error = validator.validate(name, value);
        setErrorValue(name, error);

        const errorValues = useEntryErrorStore.getState().getValues();
        const updatedErrors = {...errorValues, [name]: error};
        const count = Object.values(updatedErrors).filter((v) => v !== '').length;
        setErrorCount(count);
    }

    const onSubmit = useCallback(() => {

        if (isProcessing) return;

        const {errors, errorCount: count} = validator.validateAll();

        for (const [name, error] of Object.entries(errors)) {
            setErrorValue(name, error);
        }
        setErrorCount(count);

        if (count > 0) {
            return;
        }

        setIsProcessing(true);

        const values = getStoreValues();

        let payload: SaveParams = {
            company_name: values.company_name,
            your_name: values.your_name,
            email: values.email,
            tel: values.tel,
            project_detail_section: values.project_detail_section,
            project_detail_section_other_text: values.project_detail_section_other_text,
            yoto_naiyo1: values.yoto_naiyo1,
            yoto_menseki1: values.yoto_menseki1,
            shinsakikan_sinseitaio: values.shinsakikan_sinseitaio,
            mitsumori_iraijino_kibonoki: values.mitsumori_iraijino_kibonoki,
            files: values.files,
        }

        entryRepository.save(payload)
            .then((res) => {
                if (res.STATUS === 'OK') {
                    setIsComplete(true);
                    useEntryStore.getState().reset();
                    const target = document.querySelector('#contact .contact-flex-right');
                    if (target) {
                        const headerHeight = document.querySelector('header')?.offsetHeight ?? 0;
                        const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 10;
                        window.scrollTo({top, behavior: 'smooth'});
                    }
                } else {
                    alert(res.MESSAGE);
                }
                setIsProcessing(false);
            });

    }, [getStoreValues, validator, setErrorValue]);

    return {
        company_name,
        your_name,
        email,
        tel,
        project_detail_section,
        project_detail_section_other_text,
        yoto_naiyo1,
        yoto_menseki1,
        shinsakikan_sinseitaio,
        mitsumori_iraijino_kibonoki,
        project_detail_section_options,
        shinsakikan_sinseitaio_options,
        mitsumori_iraijino_kibonoki_options,
        agree,

        company_name_error,
        your_name_error,
        email_error,
        tel_error,
        project_detail_section_error,
        yoto_naiyo1_error,
        yoto_menseki1_error,
        shinsakikan_sinseitaio_error,
        mitsumori_iraijino_kibonoki_error,
        agree_error,

        errorCount,
        setValue,
        onSubmit,
        isProcessing,
        isComplete,
    };

};
